import posthog from 'posthog-js';
import { emitter } from 'shared/helpers/mitt';
import { ANALYTICS_RESET } from 'dashboard/constants/appEvents';

const UUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const RESET_KEY = 'vartana_product_auth_reset_v1';
const USER_KEY = 'vartana_product_auth_user_v1';
const canonicalUser = user =>
  user?.accounts?.find(account =>
    UUID.test(account.analytics_identity?.user_id)
  )?.analytics_identity.user_id;
const CONTEXT_KEYS = [
  'user_id',
  'company_id',
  'account_id',
  'company_label',
  'acquisition_id',
  'is_test',
  'test_origin',
  'test_run_id',
  'schema_version',
  'environment',
  'app_version',
  'surface',
  'source',
  'actor_type',
];
const SDK_KEYS = [
  '$session_id',
  '$window_id',
  '$device_id',
  '$lib',
  '$lib_version',
  '$browser',
  '$browser_version',
  '$os',
  '$os_version',
  '$device_type',
  '$screen_height',
  '$screen_width',
  '$viewport_height',
  '$viewport_width',
  '$anon_distinct_id',
  '$is_identified',
  '$process_person_profile',
];

// Route definitions are trusted application paths. Dynamic values and query/hash
// strings can contain customer names, search text or authentication tokens.
export const analyticsPagePath = route => route.matched?.at(-1)?.path || '/app';

export const createProductAnalytics = ({
  client = posthog,
  config,
  isCurrentAuthentication = () => true,
} = {}) => {
  let sdk = client;
  let initialized = false;
  let context = null;
  let pagePath = '/app';
  let suspended = false;

  const reset = () => {
    context = null;
    try {
      // This SDK restores its initial bootstrap on reset(true). Explicitly
      // clear it so logout cannot resurrect the first authenticated Person.
      if (initialized) sdk.reset({ resetDeviceID: true, bootstrap: {} });
    } catch {
      // eslint-disable-next-line no-console
      console.warn('vartana_product_analytics_frontend_reset_failed');
    }
  };

  const beforeSend = event => {
    if (!event || !context || suspended || !isCurrentAuthentication())
      return null;
    // The backend owns Group profiles. Recent SDKs emit $groupidentify even
    // without profile properties; never send a stripped/malformed mutation.
    if (['$groupidentify', '$create_alias', '$set'].includes(event.event))
      return null;
    delete event.$set;
    delete event.$set_once;
    delete event.$unset;
    const properties = Object.fromEntries(
      SDK_KEYS.filter(key => event.properties?.[key] !== undefined).map(key => [
        key,
        event.properties[key],
      ])
    );
    event.properties = {
      ...properties,
      ...context,
      // Required ingestion routing, not an auth token or caller-supplied field.
      token: config.projectKey,
      distinct_id: context.user_id,
      $groups: { company: context.company_id },
      $current_url: `${window.location.origin}${pagePath}`,
      $pathname: pagePath,
      page_path: pagePath,
      $referrer: '',
      $ip: null,
      $geoip_disable: true,
      event_id: event.uuid || crypto.randomUUID(),
      occurred_at: new Date().toISOString(),
    };
    return event;
  };

  const sync = (user, route) => {
    if (suspended || !isCurrentAuthentication()) return false;
    const sameUser = user?.accounts?.some(
      account => account.analytics_identity?.user_id === context?.user_id
    );
    const accountId =
      route.params?.accountId ??
      (sameUser ? context?.account_id : undefined) ??
      user?.account_id;
    const account = user?.accounts?.find(item => item.id === Number(accountId));
    const identity = account?.analytics_identity;
    if (
      !config?.projectKey ||
      !config.apiHost ||
      !user?.id ||
      !UUID.test(identity?.user_id) ||
      !UUID.test(identity?.company_id) ||
      identity.account_id !== account.id
    ) {
      if (context) reset();
      return false;
    }
    const previous = context;
    const next = {
      schema_version: '1.0',
      environment: config.environment,
      app_version: config.appVersion,
      surface: 'chat',
      source: 'frontend',
      actor_type: 'user',
      user_id: identity.user_id,
      company_id: identity.company_id,
      account_id: identity.account_id,
      is_test: identity.is_test === true,
    };
    ['company_label', 'acquisition_id', 'test_origin', 'test_run_id'].forEach(
      key => {
        if (identity[key]) next[key] = identity[key];
      }
    );
    pagePath = analyticsPagePath(route);
    if (!initialized) {
      // No automatic events before the authenticated identity and group are ready.
      sdk = sdk.init(
        config.projectKey,
        {
          api_host: config.apiHost,
          capture_pageview: false,
          capture_pageleave: false,
          autocapture: false,
          capture_exceptions: false,
          capture_performance: false,
          capture_dead_clicks: false,
          rageclick: false,
          disable_session_recording: true,
          advanced_disable_flags: true,
          disable_surveys: true,
          disable_conversations: true,
          disable_product_tours: true,
          save_campaign_params: false,
          save_referrer: false,
          person_profiles: 'identified_only',
          // Authenticated context is tab-local. No other tab can overwrite its
          // Person/Group or donate an anonymous acquisition from a shared cookie.
          bootstrap: { distinctID: next.user_id, isIdentifiedID: true },
          persistence: 'sessionStorage',
          persistence_name: `vartana_product_${config.projectKey}`,
          before_send: beforeSend,
        },
        'vartana_product'
      );
      initialized = true;
    }
    const storedId = sdk.get_distinct_id();
    if (
      (previous && previous.user_id !== next.user_id) ||
      (!previous && storedId !== next.user_id)
    ) {
      reset();
    }
    context = next;
    CONTEXT_KEYS.forEach(key => sdk.unregister(key));
    sdk.register(context);
    sdk.identify(context.user_id);
    if (!previous || previous.company_id !== context.company_id)
      sdk.resetGroups();
    sdk.group('company', context.company_id);
    return true;
  };

  return {
    reset,
    suspend() {
      suspended = true;
      reset();
    },
    authenticated() {
      suspended = false;
    },
    page(user, route) {
      try {
        if (sync(user, route))
          sdk.capture('$pageview', { page_path: pagePath });
      } catch {
        context = null;
        // Do not log exception messages: SDK/browser errors can include private URLs.
        // eslint-disable-next-line no-console
        console.warn('vartana_product_analytics_frontend_failed');
      }
    },
  };
};

export const installProductAnalytics = ({
  router,
  store,
  client = posthog,
}) => {
  const readHint = key => {
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  };
  let resetVersion = readHint(RESET_KEY);
  let authenticatedUser;
  const analytics = createProductAnalytics({
    client,
    config: window.globalConfig?.VARTANA_PRODUCT_ANALYTICS,
    // Check at capture time too: storage events from another tab can arrive
    // after navigation. Only a fresh authenticated response resumes capture.
    isCurrentAuthentication: () => {
      const sharedUser = readHint(USER_KEY);
      return (
        readHint(RESET_KEY) === resetVersion &&
        (!sharedUser ||
          sharedUser === canonicalUser(store.getters.getCurrentUser))
      );
    },
  });
  router.afterEach((to, _from, failure) => {
    const user = store.getters.getCurrentUser;
    if (!failure) analytics.page(user, to);
  });
  const resetAllTabs = () => {
    analytics.suspend();
    try {
      // Invalidation only: no account, credentials, recipient or QA context.
      window.localStorage.removeItem(USER_KEY);
      window.localStorage.setItem(RESET_KEY, crypto.randomUUID());
    } catch {
      // Storage may be denied. This tab still stops capturing immediately.
    }
  };
  store.subscribe(mutation => {
    if (mutation.type === 'CLEAR_USER') resetAllTabs();
    if (mutation.type === 'SET_CURRENT_USER') {
      const userId = canonicalUser(store.getters.getCurrentUser);
      const sharedUser = readHint(USER_KEY);
      const resetChanged = readHint(RESET_KEY) !== resetVersion;
      const anotherTabChangedUser =
        authenticatedUser && sharedUser && sharedUser !== authenticatedUser;
      // A response already in flight at logout/user-switch must not restore the
      // obsolete identity. A new document or a response matching the new login
      // can resume; neither a hint nor elapsed time authenticates anyone.
      if ((resetChanged || anotherTabChangedUser) && sharedUser !== userId) {
        analytics.suspend();
        return;
      }
      resetVersion = readHint(RESET_KEY);
      authenticatedUser = userId;
      analytics.authenticated();
      try {
        if (userId) window.localStorage.setItem(USER_KEY, userId);
      } catch {
        // The server response remains authoritative when storage is unavailable.
      }
    }
  });
  window.addEventListener('storage', event => {
    if (event.key === RESET_KEY) analytics.suspend();
    if (
      event.key === USER_KEY &&
      event.newValue &&
      event.newValue !== canonicalUser(store.getters.getCurrentUser)
    )
      analytics.suspend();
  });
  emitter.on(ANALYTICS_RESET, resetAllTabs);
  return analytics;
};
